import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Users, BadgeCheck, ShieldX, Wallet, AlertTriangle, Store } from 'lucide-react';

export function EventOptionsCards({
  serviceFeeEnabled,
  setServiceFeeEnabled,
  isServiceFeeEligible,
  isNonRefundable,
  setIsNonRefundable,
  absorbFees,
  setAbsorbFees,
  collectVendorName,
  setCollectVendorName
}) {
  return (
    <>
      {isServiceFeeEligible ? (
        <Card className="border-border bg-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-medium">On-Site Event Services</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Add 13.5% service fee for professional event support
                </p>
                {serviceFeeEnabled && (
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <BadgeCheck className="w-4 h-4 text-green-500" />
                      <span>On-site staff for ticket scanning</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <BadgeCheck className="w-4 h-4 text-green-500" />
                      <span>Wristband distribution for attendees</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <BadgeCheck className="w-4 h-4 text-green-500" />
                      <span>In-person ticket sales at venue</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border/50">
                      Total platform fee: 5.5% + 13.5% = 19%
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-2">
                      <AlertTriangle className="w-3 h-3" />
                      Minimum 50 ticket purchases required
                    </p>
                  </div>
                )}
              </div>
              <Switch
                checked={serviceFeeEnabled}
                onCheckedChange={setServiceFeeEnabled}
                data-testid="service-fee-switch"
              />
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border bg-card opacity-60">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-medium text-muted-foreground">On-Site Event Services</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  This service is only available for events in Canada and Nigeria
                </p>
              </div>
              <Switch
                checked={false}
                disabled={true}
                data-testid="service-fee-switch-disabled"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Non-Refundable Event Option */}
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <ShieldX className="w-5 h-5 text-red-500" />
                <h3 className="font-medium">Non-Refundable Event</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Disable refunds for this event
              </p>
              {isNonRefundable && (
                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Users will see a warning that tickets cannot be refunded
                  </p>
                </div>
              )}
            </div>
            <Switch
              checked={isNonRefundable}
              onCheckedChange={setIsNonRefundable}
              data-testid="non-refundable-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Fee Absorption Option */}
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-5 h-5 text-amber-500" />
                <h3 className="font-medium">Pass Fees to Buyers</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {absorbFees 
                  ? "Currently: You absorb the platform fee (deducted from your revenue)"
                  : "Currently: Buyers pay the platform fee on top of ticket price"
                }
              </p>
              {!absorbFees && (
                <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Platform fee (5.5%) will be added to buyer's total. Service fee (13.5%) is always deducted from your revenue.
                  </p>
                </div>
              )}
            </div>
            <Switch
              checked={!absorbFees}
              onCheckedChange={(checked) => setAbsorbFees(!checked)}
              data-testid="pass-fees-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Vendor / Store Name — collected PER-TICKET at checkout from the buyer.
          The organizer only decides here whether to collect it; the actual
          name is entered by the buyer on the checkout form. */}
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Store className="w-5 h-5 text-indigo-500" />
                <h3 className="font-medium">Collect Vendor / Store Name at Checkout</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                For Food &amp; Drinks or marketplace events. When enabled, each
                buyer enters a vendor/store name on the checkout form (one per
                purchase). The name is attached to the payment gateway
                metadata (Stripe &amp; Paystack) for reconciliation and shows
                on your organizer/admin transaction view — never on buyer
                receipts.
              </p>
            </div>
            <Switch
              checked={collectVendorName}
              onCheckedChange={setCollectVendorName}
              data-testid="collect-vendor-name-switch"
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
